import { FC, useCallback, useEffect, useRef, useState } from "react";
import images from "../../../assets/images";
import './AssignmentCard.scss';
import { useAppSelector } from "../../../redux/app/hooks";
import { STUDENT_ROLE, SUBMITTED_ASSIGNMENTS_TEXT, TEACHER_ROLE } from "../../../utils/constants";
import { assignment } from "../Assignments";
import { LinearProgress, Tooltip } from "@mui/material";
import { Buffer } from 'buffer';

type AssignmentCardType = {
    data: assignment;
    fileExists: boolean | undefined;
    reload: ()=>void;
    tabName: string;
    subCategory?: string;
    subject?: string;
    handleClickShareDelete: (action: string, item: assignment) => void;
    handleClickCard: (item: assignment) => void;
    image: string;
}

const AssignmentCard: FC<AssignmentCardType> = ({ image, data, tabName,fileExists, reload,handleClickShareDelete, handleClickCard, subCategory, subject}) => {
    const { deleteIcon, forwardBlack, ResetCode, DownloadSuccess, DownloadFailed, CloudDownload, docsImg, powerpoint, excelPreview, csvImg, pdfImg } = images;
    const role = useAppSelector((state) => state.auth.role);
    const [downloadProgress, setDownloadProgress] = useState<{ [key: string]: number }>({});
    const [isDownloading, setIsDownloading] = useState<{ [key: string]: boolean }>({});
    const [downloadComplete, setDownloadComplete] = useState<{ [key: string]: boolean }>({});
    const [downloadFailed, setDownloadFailed] = useState<{ [key: string]: boolean }>({});
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const profile = useAppSelector((state) => state.auth.adminProfile);

    const handleClick = (e: React.MouseEvent<HTMLElement>, action: string) => {
        e.stopPropagation();
        handleClickShareDelete(action, data)
    }

    const handleDownload = async (assignment: assignment) => {
         if (window?.electron && window.electron.isElectron()) {
            const materialId = assignment?.filepath;
            let url = baseUrl + assignment.filepath;

            // Extract the file extension
            const parts = assignment.filepath.split('.');
            const extension = parts.length > 1 ? parts?.pop() : undefined; // Safely get the extension
            
            // Check for specific extensions and change the URL if necessary
            if (extension?.toLowerCase() && ['doc', 'docx', 'ppt', 'pptx'].includes(extension?.toLowerCase())) {
                // Change the URL to end with .pdf
                url = url.replace(/\.(doc|docx|ppt|pptx)$/, '.pdf');
            }
            try {
                console.log('materials==========>', assignment, 'subcategory=======>', subCategory, "url for download======>",url);
    
                // Show initial download states
                setIsDownloading((prev) => ({ ...prev, [materialId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                setDownloadFailed((prev) => ({ ...prev, [materialId]: false }));
    
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error('File download failed');
                }
    
                // Get file blob
                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                const fileData = Buffer.from(arrayBuffer);
    
                // Calculate total file size for progress tracking
                const fileSize = fileData.length;
                let downloadedSize = 0;
                // Send the file metadata and file data to the main process to store in SQLite
                const saveResponse = await window.electron.saveMaterial({
                    fileId: assignment.fileId,
                    userId: profile?.getCurrentUser?.nodes[0]?.uId,
                    filename: assignment.filename,
                    filetype: assignment.filetype,
                    filepath: assignment.filepath,
                    filesize: assignment.filesize,
                    filedata: fileData,
                    fileLocation: 'Assignments',
                    subject,
                }, url);

                if (saveResponse.success === false){
                    throw new Error(saveResponse.message || 'Error saving material to SQLite');
                }

                // Simulate progress: for Electron, the download is complete at once, so we simulate chunk progress
                const interval = setInterval(async() => {
                    if (downloadedSize < fileSize) {
                        downloadedSize += fileSize / 10; // Simulate 10% increments
                        const progress = Math.min(Math.floor((downloadedSize / fileSize) * 100), 95);
                        setDownloadProgress((prev) => ({ ...prev, [materialId]: progress }));
                    } else {
                        clearInterval(interval);
    
                        // Set final progress to 100% and mark download as complete
                        setDownloadProgress((prev) => ({ ...prev, [materialId]: 100 }));
                        setDownloadComplete((prev) => ({ ...prev, [materialId]: true }));
                         // Check if the file exists after the download
                        reload();
                         
                        // Reset state after a delay
                        setTimeout(() => {
                            setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                            setIsDownloading((prev) => ({ ...prev, [materialId]: false }));
                            setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                        }, 5000);
                    }
                }, 300); // Simulate progress every 300ms
    
                console.log('Download and metadata storage successful');
            } catch (error) {
                console.error('Download error:', error);
                setIsDownloading((prev) => ({ ...prev, [assignment.filepath]: false }));
                setDownloadFailed((prev) => ({ ...prev, [assignment.filepath]: true }));
                setTimeout(() => {
                    setDownloadFailed((prev) => ({ ...prev, [assignment.filepath]: false }));
                }, 5000);
            }
        } else {
            try {
                const materialId = assignment?.filepath;
                setIsDownloading((prev) => ({ ...prev, [materialId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                setDownloadFailed((prev) => ({ ...prev, [materialId]: false }));
    
                const url = baseUrl + assignment?.filepath;
                const response = await fetch(url);
                if (!response.ok) {
                    console.error('Failed to fetch the file', response.statusText);
                    setIsDownloading((prev) => ({ ...prev, [materialId]: false }));
                    setDownloadFailed((prev) => ({ ...prev, [materialId]: true }));
                    setTimeout(() => {
                        setDownloadFailed((prev) => ({ ...prev, [materialId]: false }));
                    }, 5000);
                    return;
                }
    
                const reader = response.body?.getReader();
                let receivedLength = 0;
                const chunks = [];
    
                while (true) {
                    const { done, value } = await reader!.read();
                    if (done) {
                        break;
                    }
    
                    chunks.push(value);
                    receivedLength += value.length;
    
                    // Incremental progress based on the number of chunks
                    setDownloadProgress((prev) => ({ ...prev, [materialId]: Math.min(prev[materialId] + 5, 95) })); // Up to 95%
                }
    
                const blob = new Blob(chunks);
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = assignment?.filepath;
                link.click();
                // Add a small delay before updating the state
                await new Promise(resolve => setTimeout(resolve, 500));
                setDownloadComplete((prev) => ({ ...prev, [materialId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [materialId]: 100 })); // Set to 100% on completion
                // Hide the progress and complete message after 5 seconds
                setTimeout(() => {
                    setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                    setIsDownloading((prev) => ({ ...prev, [materialId]: false }));
                    setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                }, 5000);
            } catch (error) {
                console.error('Download error:', error);
                setIsDownloading((prev) => ({ ...prev, [assignment.filepath]: false }));
                setDownloadFailed((prev) => ({ ...prev, [assignment.filepath]: true }));
                setTimeout(() => {
                    setDownloadFailed((prev) => ({ ...prev, [assignment.filepath]: false }));
                }, 5000);
            }
        }
    };

    const textRef = useRef<HTMLDivElement>(null);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  
    const checkEllipsis = useCallback(() => {
      if (textRef.current) {
        setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    }, []);
  
    useEffect(() => {
      checkEllipsis();
      window.addEventListener("resize", checkEllipsis);
      return () => window.removeEventListener("resize", checkEllipsis);
    }, [checkEllipsis, data?.filename]);

    return (
        <div className={`AssignmentCard`} onClick={() => handleClickCard(data)}>
            <div className="CardContainer">
                <div className={`AssignmentCardImg ${ data?.filetype === "pdf" ? "pdfCardImg" : data?.filetype === "csv" ? "csvCardImg" : "" }`}>
                    <img 
                        src={ (data?.filetype === "docx" || data?.filetype === "doc") 
                             ? docsImg 
                             : (data?.filetype === "ppt" || data?.filetype === "pptx") 
                               ? powerpoint 
                               : data?.filetype === 'pdf' 
                                 ? pdfImg 
                                 : data?.filetype === "csv" 
                                   ? csvImg 
                                   : (data?.filetype === "xls" || data?.filetype === "xlsx") 
                                     ? excelPreview 
                                     : pdfImg 
                            } 
                            className="CardImg" 
                            alt="CardImg" 
                    />
                </div>
                {((role === TEACHER_ROLE && tabName === "Assignment")||(role === STUDENT_ROLE && tabName === SUBMITTED_ASSIGNMENTS_TEXT && !data.isExpiry)) && (
                    <div className="deleteIcon" onClick={(e) => handleClick(e, "delete")}>
                        <img src={deleteIcon} className="deleteIconImg" alt="deleteIcon" />
                    </div>
                )}
            </div>
            <div className="assignmentCardProgressCtn">
                            {(isDownloading[data?.filepath]) && (
                                <LinearProgress variant="determinate" value={downloadProgress[data?.filepath]}
                                    style={{ backgroundColor: '#ECECEC', height: '2px' }}
                                    sx={{
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: '#4086F4',
                                            height: '2px'
                                        },
                                    }} />
                             )}
            </div>
            <div className="AssignmentNameContiner">
            <Tooltip title={isEllipsisActive ? data?.filename : ""}>
      <div ref={textRef} className="AssignmentName">
        {data?.filename}
      </div>
    </Tooltip>
                {((role === TEACHER_ROLE && tabName === "Assignment")||(role === STUDENT_ROLE && tabName === SUBMITTED_ASSIGNMENTS_TEXT && !data.isExpiry)) && (
                    <div className="forwardIcon">
                        <img 
                            src={ data?.isClassroom ? ResetCode : forwardBlack} 
                            className="forwardIconImg" 
                            alt="forwardIcon"  
                            onClick={(e) => handleClick(e, data?.isClassroom ? "unshare" : "share")}
                        />
                    </div>
                )}
                {((role === STUDENT_ROLE && tabName === "Assignment")||(role === TEACHER_ROLE && tabName === SUBMITTED_ASSIGNMENTS_TEXT)) && (
                <div
                        className="forwardIcon"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!fileExists) {
                                handleDownload(data);
                            } else {
                                handleClickCard(data);
                            }
                        }}
                    >
                        {downloadComplete[data?.filepath] ? (
                            <img src={DownloadSuccess} alt="DownloadSuccess" />
                        ) : downloadFailed[data?.filepath] ? (
                            <img src={DownloadFailed} alt="DownloadFailed" />
                        ) : fileExists ? (
                            <img src={DownloadSuccess} alt="AlreadyDownloaded" />
                        ) : (
                            <img src={CloudDownload} alt="CloudDownload" />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default AssignmentCard;
